export const PRICING_MODE = {
  CHECK_STANDARD: 'check_standard',
  CHECK_VARIABLE: 'check_variable',
  CHECK_ITEM_FIXED: 'check_item_fixed',
  CHECK_ITEM: 'check_item',
  SUBCHECK_STANDARD: 'subcheck_standard',
  SUBCHECK_VARIABLE: 'subcheck_variable',
  SUBCHECK_FIXED: 'subcheck_fixed',
};

const PRICING_MODE_VALUES = new Set(Object.values(PRICING_MODE));

export function normalizePricingMode(mode) {
  return PRICING_MODE_VALUES.has(mode) ? mode : PRICING_MODE.CHECK_STANDARD;
}

export function allowsCustomPricing(mode) {
  return [
    PRICING_MODE.CHECK_STANDARD,
    PRICING_MODE.CHECK_VARIABLE,
    PRICING_MODE.CHECK_ITEM,
    PRICING_MODE.SUBCHECK_STANDARD,
    PRICING_MODE.SUBCHECK_VARIABLE,
  ].includes(normalizePricingMode(mode));
}

export function supportsVariableCustomPricing(mode) {
  return [PRICING_MODE.CHECK_VARIABLE, PRICING_MODE.SUBCHECK_VARIABLE].includes(
    normalizePricingMode(mode),
  );
}

export function isFixedPricingMode(mode) {
  return [PRICING_MODE.CHECK_ITEM_FIXED, PRICING_MODE.SUBCHECK_FIXED].includes(
    normalizePricingMode(mode),
  );
}

export function allowsVolumeDiscount(mode) {
  return [PRICING_MODE.CHECK_ITEM, PRICING_MODE.SUBCHECK_STANDARD].includes(
    normalizePricingMode(mode),
  );
}

export function isCheckItemMode(mode) {
  return normalizePricingMode(mode) === PRICING_MODE.CHECK_ITEM;
}

export function supportsBillPerItem(mode, hasSubchecks = false) {
  return isCheckItemMode(mode) && !hasSubchecks;
}

export function getDefaultPricingSubtitle(billingCurrency, defaultPrice, pricingMode) {
  const mode = normalizePricingMode(pricingMode);
  if (mode === PRICING_MODE.SUBCHECK_FIXED) {
    return null;
  }
  const currency = billingCurrency ? String(billingCurrency).toUpperCase() : null;
  const defaultNum = defaultPrice != null && defaultPrice !== '' ? Number(defaultPrice) : NaN;
  const perSubcheckSuffix =
    mode === PRICING_MODE.SUBCHECK_STANDARD || mode === PRICING_MODE.SUBCHECK_VARIABLE
      ? ' per subcheck'
      : '';

  if (currency && Number.isFinite(defaultNum)) {
    return `The default price for this check type is ${defaultNum.toFixed(2)} ${currency}${perSubcheckSuffix}.`;
  }
  if (currency) {
    return 'No default price set for this check type in the pricing table.';
  }
  return 'Billing currency is not set for this company.';
}

export function getFixedPricingCustomPricingBlockedMessage(pricingMode) {
  const mode = normalizePricingMode(pricingMode);
  if (mode === PRICING_MODE.SUBCHECK_FIXED) {
    return 'Custom pricing is not available for this check type. Each subcheck is charged at the fixed price that matches its country. See the Admin Pricing page for more information.';
  }
  if (mode === PRICING_MODE.CHECK_ITEM_FIXED) {
    return 'Custom pricing is not available for this check type. The amount charged depends on the options selected when the check is launched. If no options are applicable, the default price is charged. See the Admin Pricing page for more information.';
  }
  return null;
}

const REFERENCE_CHECK_SLUGS = ['reference', 'custom_reference'];

export function getBillPerItemLabel(checkSlug) {
  if (REFERENCE_CHECK_SLUGS.includes(checkSlug)) {
    return 'Bill per item (per completed referee)';
  }
  return 'Bill per item (per name searched)';
}

export function getBillPerItemTooltip(checkSlug) {
  if (REFERENCE_CHECK_SLUGS.includes(checkSlug)) {
    return 'When on, billing is one line per completed referee. When off, one line at the default price.';
  }
  return 'When on, billing is one line per name searched. When off, one line at the default price.';
}
